/**
 * SA Tools - ROI Summary Table
 * Display calculated ROI metrics for all three scenarios
 */
import React from 'react';
import { Paper, Title, Table, Text, Stack } from '@mantine/core';

function ROISummaryTable({ processes, contextData }) {
  const cases = ['Conservative', 'Likely', 'Optimistic'];
  const costPerAgent = contextData?.cost_per_agent || 25;
  const professionalServices = contextData?.professional_services_price || 0;
  const bomPrice = contextData?.bom_price || 0;
  const bomLengthMonths = contextData?.bom_length_months || 1;
  const monthlyUsageCosts = contextData?.monthly_usage_costs || 0;

  const monthlyBOMCost = bomLengthMonths > 0 ? bomPrice / bomLengthMonths : 0;
  const totalMonthlyCosts = monthlyBOMCost + monthlyUsageCosts;

  // Format number with commas for display
  const formatNumberWithCommas = (value) => {
    if (!value && value !== 0) return '0.00';
    const numStr = value.toString();
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  // Default values
  const defaultProbabilities = {
    'Conservative': '65%',
    'Likely': '80%',
    'Optimistic': '95%'
  };
  const defaultValues = {
    automation_potential: '80%',
    number_of_interactions: '10000',
    avg_time_per_interaction: '30 sec'
  };

  // Parse time string to seconds
  const parseTimeToSeconds = (timeStr) => {
    if (!timeStr) return 0;
    const numMatch = timeStr.match(/[\d.]+/);
    if (!numMatch) return 0;
    const value = parseFloat(numMatch[0]);
    const lowerStr = timeStr.toLowerCase();
    if (lowerStr.includes('hour') || lowerStr.includes('hr') || lowerStr.includes('h')) {
      return value * 3600;
    } else if (lowerStr.includes('min') || lowerStr.includes('m')) {
      return value * 60;
    } else if (lowerStr.includes('sec') || lowerStr.includes('s')) {
      return value;
    }
    return value;
  };

  const calculateAutomationWithProbability = (probability, automationPotential) => {
    if (!probability || !automationPotential) return 0;
    const probValue = parseFloat(probability) / 100;
    const autoValue = parseFloat(automationPotential) / 100;
    return probValue * autoValue;
  };

  const calculateTotalInteractions = (numberOfInteractions, automationWProbability) => {
    if (!numberOfInteractions || !automationWProbability) return 0;
    const interactions = parseFloat(numberOfInteractions);
    return interactions * automationWProbability;
  };

  const calculateTotalTimeSaved = (totalInteractions, avgTimePerInteraction) => {
    if (!totalInteractions || !avgTimePerInteraction) return 0;
    const timeInSeconds = parseTimeToSeconds(avgTimePerInteraction);
    return totalInteractions * timeInSeconds;
  };

  // Calculate total savings for each case
  const savingsByCase = {};
  cases.forEach((caseType) => {
    savingsByCase[caseType] = processes.reduce((sum, process) => {
      const caseData = process.roi_cases?.[caseType] || {};

      const automationWProbability = calculateAutomationWithProbability(
        caseData.probability || defaultProbabilities[caseType],
        caseData.automation_potential || defaultValues.automation_potential
      );
      const totalInteractions = calculateTotalInteractions(
        caseData.number_of_interactions || defaultValues.number_of_interactions,
        automationWProbability
      );
      const totalTimeSaved = calculateTotalTimeSaved(
        totalInteractions,
        caseData.avg_time_per_interaction || defaultValues.avg_time_per_interaction
      );

      const timeInHours = parseFloat(totalTimeSaved) / 3600;
      const savings = timeInHours * costPerAgent;

      return sum + savings;
    }, 0);
  });

  // Calculate ROI metrics for each case
  const roiMetrics = {};
  cases.forEach((caseType) => {
    const grossMonthlySavings = savingsByCase[caseType];
    const netMonthlySavings = grossMonthlySavings - totalMonthlyCosts;
    const monthsToPayoff = netMonthlySavings > 0
      ? professionalServices / netMonthlySavings
      : Infinity;

    roiMetrics[caseType] = {
      grossMonthlySavings,
      netMonthlySavings,
      monthsToPayoff: monthsToPayoff < 100 ? monthsToPayoff.toFixed(1) : '100+'
    };
  });

  if (processes.length === 0) {
    return (
      <Paper shadow="sm" p="xl" withBorder>
        <Text c="dimmed" ta="center">
          No processes available. Please add processes to calculate ROI.
        </Text>
      </Paper>
    );
  }

  return (
    <Paper shadow="sm" p="xl" radius="md">
      <Stack gap="md">
        <Title order={2} c="purple">ROI Summary</Title>

        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Scenario</Table.Th>
              <Table.Th>Gross Monthly Savings</Table.Th>
              <Table.Th>Monthly Costs</Table.Th>
              <Table.Th>Net Monthly Savings</Table.Th>
              <Table.Th>Months to Payoff</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {cases.map((caseType) => {
              const metrics = roiMetrics[caseType];
              const color = caseType === 'Conservative' ? 'red' : caseType === 'Likely' ? 'yellow' : 'green';

              return (
                <Table.Tr key={caseType}>
                  <Table.Td>
                    <Text fw={600} c={color}>
                      {caseType}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text>${formatNumberWithCommas(metrics.grossMonthlySavings.toFixed(2))}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text>${formatNumberWithCommas(totalMonthlyCosts.toFixed(2))}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={600} c={metrics.netMonthlySavings > 0 ? 'green' : 'red'}>
                      ${formatNumberWithCommas(metrics.netMonthlySavings.toFixed(2))}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text>
                      {metrics.monthsToPayoff === '100+' ? '100+ months' : `${metrics.monthsToPayoff} months`}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        <Text size="sm" c="dimmed">
          Professional Services Cost: ${formatNumberWithCommas(professionalServices.toFixed(2))}
        </Text>
      </Stack>
    </Paper>
  );
}

export default ROISummaryTable;
